import { SEARCH_MIN_QUERY_LENGTH } from "../constants";

import type {
  SearchEngineSearchOptions,
  SearchResult,
  SearchResultType,
} from "./types";

/**
 * Base class of all search engines used by the generic Search component.
 *
 * An engine is responsible for querying one api and for normalizing its
 * response to a list of {@link SearchResult}.
 */
abstract class SearchEngine<TFeature = unknown> {
  abstract readonly id: string;

  /** Minimum number of characters before a request is sent. */
  readonly minQueryLength: number = SEARCH_MIN_QUERY_LENGTH;

  /**
   * Only the results of the engine with the lowest priority value are
   * displayed first in the unified result list.
   */
  readonly priority: number = 0;

  constructor(options: { priority?: number } = {}) {
    if (options.priority !== undefined) {
      this.priority = options.priority;
    }
  }

  canSearch(query: string): boolean {
    return (query || "").trim().length >= this.minQueryLength;
  }

  /**
   * Runs the search and returns normalized results. Returns an empty list when
   * the query is too short.
   */
  async search(
    query: string,
    options: SearchEngineSearchOptions,
  ): Promise<SearchResult<TFeature>[]> {
    if (!this.canSearch(query)) {
      return [];
    }
    return this.doSearch(query.trim(), options);
  }

  protected abstract doSearch(
    _query: string,
    _options: SearchEngineSearchOptions,
  ): Promise<SearchResult<TFeature>[]>;
}

export type { SearchEngineSearchOptions, SearchResult, SearchResultType };

export default SearchEngine;
